import React, { useEffect, useMemo, useState } from "react";
import "./EAProductBanner.scss";
import EASkeletonLoader from "../EASkeletonLoader";
import { useTranslation } from "react-i18next";
import { MdImageNotSupported } from "react-icons/md";
import { IoCalendarClearOutline, IoChevronDown, IoLocationOutline } from "react-icons/io5";
import EAButton, { EAButtonAnimation } from "../Buttons/EAButton";
import EAModal from "../Modal/EAModal";
import useAuth from "../../Login/hooks/useAuth";
import { LOGIN_PAGE_WITH_RETURN } from "../../../routes";
import { useNavigate } from "react-router";
import { stripHtml } from "../../../../utils/stripHtml";
import { parseBannerPrice } from "./parseBannerPrice";
import { format } from "date-fns";
import useLocaleByLanguage from "../../../../utils/hooks/useLocaleByLanguage";

interface IProps {
  title: string;
  description: string;
  pictureUrl: string;
  buttonTitle: string;
  buttonStartIcon?: React.ReactNode;
  buttonEndIcon?: React.ReactNode;
  buttonVariant?: "filled" | "outlined" | "text";
  mainPrice: string | number;
  /** Tekst ceny pochodzi z konfiguracji stepSale — zostawiamy wpisany podpis. */
  configuredPrice?: boolean;
  additionalPriceInfo?: string;
  onClick?: () => void;
  loading?: boolean;
  expanded?: boolean;
  children?: React.ReactNode;
  highlightBanner?: boolean;
  bannerLabel?: string;
  crossedPrice?: string;
  highlightedFeatures?: string[];
  loginRequired?: boolean;
  hidePrice?: boolean;
  buttonDisabled?: boolean;
  buttonLoading?: boolean;
  buttonCaption?: string;
  buttonAnimationType?: EAButtonAnimation;
  eventDate?: number | Date;
  /** Miejsce wydarzenia pokazywane obok daty nad tytułem. */
  eventLocation?: string;
}

const EAProductBanner: React.FC<IProps> = ({
  title,
  description,
  pictureUrl,
  loading = false,
  buttonTitle,
  buttonStartIcon,
  buttonEndIcon,
  buttonVariant = "filled",
  mainPrice,
  configuredPrice = false,
  additionalPriceInfo,
  onClick = () => {},
  expanded = false,
  children,
  highlightBanner = false,
  bannerLabel,
  crossedPrice,
  highlightedFeatures = [],
  loginRequired = false,
  hidePrice = false,
  buttonDisabled = false,
  buttonLoading = false,
  buttonCaption,
  buttonAnimationType,
  eventDate,
  eventLocation,
}) => {
  const { t, i18n } = useTranslation();
  const locale = useLocaleByLanguage();
  const [readMore, setReadMore] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [loginRequiredModalOpen, setLoginRequiredModalOpen] = useState(false);
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    setImageFailed(false);
  }, [pictureUrl]);

  const descriptionPreview = useMemo(() => stripHtml(description), [description]);
  const hasDescription = descriptionPreview.length > 0;

  const formattedEventDate = useMemo(() => {
    if (!eventDate) return null;
    const dateObj = new Date(eventDate);
    if (Number.isNaN(dateObj.getTime())) return null;

    const isDifferentYear = dateObj.getFullYear() !== new Date().getFullYear();
    const dayAndMonth = format(dateObj, isDifferentYear ? "d MMMM yyyy" : "d MMMM", { locale });
    const weekdayRaw = format(dateObj, "EEEE", { locale });
    const weekday = weekdayRaw.charAt(0).toUpperCase() + weekdayRaw.slice(1);
    const time = format(dateObj, "HH:mm");

    return {
      dayAndMonth,
      weekday,
      time,
      iso: dateObj.toISOString(),
    };
  }, [eventDate, locale]);

  const locationLabel = eventLocation?.trim() ?? "";
  const hasLocation = locationLabel.length > 1;

  const priceLabels = useMemo(
    () => ({
      pricesFrom: t("ceny_od"),
      from: t("od"),
      currency: t("zl"),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language],
  );
  const priceDisplay = useMemo(
    () => parseBannerPrice(mainPrice, priceLabels, { preserveCaption: configuredPrice }),
    [mainPrice, priceLabels, configuredPrice],
  );
  const crossedDisplay = useMemo(
    () => (crossedPrice ? parseBannerPrice(crossedPrice, priceLabels) : null),
    [crossedPrice, priceLabels],
  );
  const crossedAmount =
    crossedDisplay?.type === "price"
      ? crossedDisplay.amount
      : crossedDisplay?.type === "text"
        ? crossedDisplay.text
        : "";
  const rawPriceText = String(mainPrice ?? "");
  const isPriceLoading = /ładowanie/i.test(rawPriceText);
  const isNoTickets = /brak biletów/i.test(rawPriceText);
  const showPurchaseButton = !isPriceLoading && !isNoTickets;

  const handleClick = () => {
    if (loginRequired && !isLoggedIn) {
      setLoginRequiredModalOpen(true);
    } else {
      onClick();
    }
  };

  if (loading) {
    return <EASkeletonLoader height="300px" />;
  }

  return (
    <>
      <div className={`product-banner-container ${highlightBanner ? "highlight" : ""}`}>
        {bannerLabel && <div className="product-banner__label">{bannerLabel}</div>}
        <div className="product-banner-primary-wrapper">
          <div className="product-banner__media">
            {pictureUrl && !imageFailed ? (
              <img
                className="product-banner__image"
                alt={title || "Miniatura zdjęcia produktu"}
                src={pictureUrl}
                onError={() => setImageFailed(true)}
              />
            ) : (
              <div className="product-banner__empty-image">
                <MdImageNotSupported />
              </div>
            )}
          </div>

          <div className="product-banner__right-col">
            <div className="product-banner__content">
              <div className="product-banner__heading">
                {(formattedEventDate || hasLocation) && (
                  <div className="product-banner__event-meta">
                    {formattedEventDate && (
                      <time className="product-banner__event-date" dateTime={formattedEventDate.iso}>
                        <IoCalendarClearOutline className="product-banner__event-date-icon" aria-hidden="true" />
                        <span>
                          {formattedEventDate.weekday}, {formattedEventDate.dayAndMonth}
                        </span>
                        <span className="product-banner__event-date-separator" aria-hidden="true">
                          ·
                        </span>
                        <span className="product-banner__event-date-time">{formattedEventDate.time}</span>
                      </time>
                    )}
                    {hasLocation && (
                      <span className="product-banner__event-location" title={locationLabel}>
                        <IoLocationOutline className="product-banner__event-date-icon" aria-hidden="true" />
                        <span>{locationLabel}</span>
                      </span>
                    )}
                  </div>
                )}
                <h4>{title}</h4>
              </div>
              {highlightedFeatures.length > 0 && (
                <div className="product-banner__content-highlighted-features">
                  {highlightedFeatures.map((item, index) => (
                    <p key={index} className="body-text-small text-bold product-banner__content-highlighted-feature">
                      {t(item)}
                    </p>
                  ))}
                </div>
              )}
              {hasDescription && <p className="product-banner__content-description">{descriptionPreview}</p>}
              {hasDescription && (
                <EAButton
                  variant="text"
                  title={t("Pokaż szczegóły")}
                  endIcon={<IoChevronDown size={16} aria-hidden="true" />}
                  noMargins
                  onClick={() => setReadMore(true)}
                  compact
                  color="dark"
                />
              )}
            </div>

            <div className="product-banner__divider" aria-hidden="true" />

            <div className={`product-banner__summary ${hidePrice ? "product-banner__summary--no-price" : ""}`}>
              {!hidePrice && isPriceLoading && (
                <div className="product-banner__price-skeleton" aria-hidden="true">
                  <EASkeletonLoader width="48px" height="12px" />
                  <EASkeletonLoader width="120px" height="32px" />
                </div>
              )}

              {!hidePrice && isNoTickets && (
                <div className="product-banner__price">
                  <span className="product-banner__price-unavailable">{t("brak_biletow")}</span>
                </div>
              )}

              {!hidePrice &&
                !isPriceLoading &&
                !isNoTickets &&
                (priceDisplay.type !== "empty" || additionalPriceInfo) && (
                  <div className="product-banner__price">
                    {priceDisplay.type === "text" && (
                      <span className="product-banner__price-custom">{priceDisplay.text}</span>
                    )}
                    {priceDisplay.type === "price" && (
                      <>
                        {priceDisplay.prefix && (
                          <span className="product-banner__price-prefix">{priceDisplay.prefix}</span>
                        )}
                        <div className="product-banner__price-row">
                          <span className="product-banner__price-amount">{priceDisplay.amount}</span>
                          {crossedAmount && <span className="product-banner__crossed-price">{crossedAmount}</span>}
                        </div>
                        {priceDisplay.suffix && (
                          <span className="product-banner__price-meta">{priceDisplay.suffix}</span>
                        )}
                      </>
                    )}
                    {additionalPriceInfo && (
                      <span className="product-banner__price-meta">{t(additionalPriceInfo)}</span>
                    )}
                  </div>
                )}

              {showPurchaseButton && (
                <div className="product-banner__button">
                  <EAButton
                    title={buttonTitle}
                    startIcon={buttonStartIcon}
                    endIcon={buttonEndIcon}
                    onClick={handleClick}
                    variant={buttonVariant}
                    disabled={buttonDisabled}
                    loading={buttonLoading}
                    animationType={buttonAnimationType}
                  />
                  {buttonCaption && (
                    <p className="caption text-color-error product-banner__button-caption">{buttonCaption}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {children && (
          <div className={`product-banner__expanded${expanded ? " is-open" : ""}`} aria-hidden={!expanded}>
            <div className="product-banner__expanded-clip">
              <div className="product-banner__expanded-content">{children}</div>
            </div>
          </div>
        )}
      </div>

      <EAModal
        isOpen={readMore}
        title={title}
        onClose={() => setReadMore(false)}
        cancelButtonLabel={t("przeczytalem")}
        hideAcceptButton
      >
        <p dangerouslySetInnerHTML={{ __html: description }}></p>
      </EAModal>

      <EAModal
        isOpen={loginRequiredModalOpen}
        title={t("ta_opcja_dostepna_tylko_po_zalogowaniu")}
        onClose={() => setLoginRequiredModalOpen(false)}
        acceptButtonLabel={t("przejdz_do_logowania_rejestracji")}
        cancelButtonLabel={t("zamknij")}
        onAccept={() => navigate(LOGIN_PAGE_WITH_RETURN)}
      >
        <p>{t("dedykowane_dla_stalych_klientow_info")}</p>
      </EAModal>
    </>
  );
};

export default EAProductBanner;
